import styled from 'styled-components';
import { rgba } from 'polished';

import { mainGridGapColor, cFFFFFF } from '#/colors';

import * as Tabs from '#/components/common/tabs';
import { gradientUnderline } from '#/components/shared/styles';

import { OrderBookProvider } from '#/features/perpetuals/order-book/order-book-context';
import { useTranslation } from '#/features/localization/utils';
import { useScreenSize } from '#/features/layout/screen-size-context';
import { MEDIA } from '#/features/layout/media-queries';

import OrderBook from './OrderBook';
import TradeTape from './TradeTape';

const TABS: readonly Tabs.TabData[] = [
  { id: 'order-book', label: <TabLabelOrderBook />, content: <OrderBook /> },
  { id: 'trade-tape', label: <TabLabelTradeTape />, content: <TradeTape /> },
];
const ORDER_BOOK_DEPTH_SM = 5;
const ORDER_BOOK_DEPTH = 10;

interface Props {
  readonly className?: string;
}

export default function OrderBookTradeTape(props: Props) {
  const { size } = useScreenSize();
  const depth = size === 'sm' ? ORDER_BOOK_DEPTH_SM : ORDER_BOOK_DEPTH;

  return (
    <OrderBookProvider depth={depth}>
      <OrderBookTradeTapeContent {...props} />
    </OrderBookProvider>
  );
}

function OrderBookTradeTapeContent({ className }: Props) {
  return (
    <Container className={className} data-testid="order-book-trade-tape">
      <TabsRoot defaultValue={TABS[0]?.id}>
        <TabsList>
          {TABS.map((tab) => (
            <TabsTrigger key={tab.id} disabled={tab.disabled} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </TabsRoot>
    </Container>
  );
}

function TabLabelOrderBook() {
  const { t } = useTranslation();
  return t('Order Book');
}

function TabLabelTradeTape() {
  const { t } = useTranslation();
  return t('Trades');
}

const Container = styled.div`
  border-left: 1px solid ${mainGridGapColor};
  border-bottom: 1px solid ${mainGridGapColor};
`;

const TabsRoot = styled(Tabs.Root)`
  display: flex;
  height: 100%;
  flex: 1 1 0%;
  flex-direction: column;
`;

const TabsTrigger = styled(Tabs.Trigger)`
  background: none;
  border: none;
  color: ${rgba(cFFFFFF, 0.5)};
  display: inline-block;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  border-bottom: none;
  margin-bottom: -1px;
  flex-grow: 1;
  z-index: 1;

  &[data-state='active'] {
    color: ${cFFFFFF};
    ${gradientUnderline}
  }
`;

const TabsList = styled(Tabs.List)`
  display: flex;
  height: 39px;
  border-bottom: 1px solid ${mainGridGapColor};
  padding: 0 2px;
`;

const TabsContent = styled(Tabs.Content)`
  flex: 1 1 0%;
  position: relative;
  z-index: 0; // allows the active tab indicator to be on top

  /* 0px – md */
  overflow: hidden;

  @media ${MEDIA.md} {
    overflow: auto;
  }
`;
